import { apiService } from './api';

export interface CompetitorBacklinkData {
  url_from: string;
  title?: string;
  domain_rating_source?: string;
  is_dofollow?: boolean;
  is_nofollow?: boolean;
  is_ugc?: boolean;
  is_sponsored?: boolean;
  is_content?: boolean;
  refdomains_source?: string;
  last_visited?: string;
  anchor?: string;
  url_to?: string;
}

export interface StoreBacklinksRequest {
  competitorWebsite: string;
  projectId: string;
  backlinks: CompetitorBacklinkData[];
}

export interface CompetitorBacklink {
  id: string;
  competitorWebsite: string;
  clientId: string;
  projectId: string;
  backlinkUrl: string;
  backlinkTitle?: string;
  backlinkDr?: number;
  backlinkDomainRating?: number;
  isDofollow: boolean;
  isNofollow: boolean;
  isUgc: boolean;
  isSponsored: boolean;
  isContent: boolean;
  referringDomains?: string;
  lastSeen?: string;
  anchorText?: string;
  targetUrl?: string;
  requestStatus: number; // 0 or 1
  /** Set after admin CSV export from DR Calculator Requests (Pending → Exported tab). */
  drRequestsExportedAt?: string | null;
  /** Client dismissed “site arrived in Guest Blog Sites” notification for this row. */
  guestBlogMatchSeenAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present on GET /requested for clients */
  project?: { id: string; projectName: string };
  referringDomain?: string;
  inGuestBlogCatalog?: boolean;
}

export const competitorBacklinksService = {
  // Store backlinks from DR Calculator
  async storeBacklinks(data: StoreBacklinksRequest): Promise<CompetitorBacklink[]> {
    const response = await apiService.post<{ success: boolean; data: CompetitorBacklink[] }>('/competitor-backlinks', data);
    return response.data;
  },

  // Get competitor backlinks for a project
  async getProjectBacklinks(projectId: string): Promise<CompetitorBacklink[]> {
    const response = await apiService.get<{ success: boolean; data: CompetitorBacklink[] }>(`/competitor-backlinks/project/${projectId}`);
    return response.data;
  },

  // Get competitor backlinks for a competitor domain (optionally scoped to project)
  async getDomainBacklinks(competitorWebsite: string, projectId?: string): Promise<CompetitorBacklink[]> {
    const params = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    const response = await apiService.get<{ success: boolean; data: CompetitorBacklink[] }>(
      `/competitor-backlinks/domain/${encodeURIComponent(competitorWebsite)}${params}`
    );
    return response.data;
  },

  // Update requestStatus from 1 to 2
  async requestBacklink(data: {
    competitorWebsite: string;
    backlinkUrl: string;
    backlinkTitle?: string;
    anchorText?: string;
    targetUrl?: string;
  }): Promise<CompetitorBacklink> {
    const response = await apiService.put<{ success: boolean; data: CompetitorBacklink }>('/competitor-backlinks/request', data);
    return response.data;
  },

  /**
   * Client only — canonical referring-page keys (normalizeBacklinkUrlKey) that another client already has in the DR request queue (requestStatus=2).
   */
  async getCrossClientRequestedUrlKeys(urls: string[]): Promise<string[]> {
    const unique = Array.from(new Set(urls.map((u) => String(u).trim()).filter(Boolean))).slice(0, 500);
    if (unique.length === 0) return [];
    const response = await apiService.post<{ success: boolean; keys: string[] }>(
      '/competitor-backlinks/cross-client-requested-url-keys',
      { urls: unique }
    );
    return Array.isArray(response.keys) ? response.keys : [];
  },

  /** Client only — DR Calculator requested rows for that client */
  async getAllRequestedBacklinks(): Promise<CompetitorBacklink[]> {
    const response = await apiService.get<{ success: boolean; data: CompetitorBacklink[] }>('/competitor-backlinks/requested');
    return response.data;
  },

  /** Client only — count of requested referring sites now in Guest Blog catalog, notification not yet dismissed */
  async getUnseenGuestBlogArrivedCount(): Promise<number> {
    const response = await apiService.get<{ success: boolean; count: number }>(
      '/competitor-backlinks/requested/unseen-guest-blog-arrived-count'
    );
    return typeof response.count === 'number' ? response.count : 0;
  },

  /** Client only — mark catalog-match notifications as seen (optional ids; default = all matching unseen) */
  async markGuestBlogMatchesSeen(ids?: string[]): Promise<{ updated: number }> {
    const response = await apiService.post<{ success: boolean; updated: number }>(
      '/competitor-backlinks/requested/mark-guest-blog-seen',
      ids?.length ? { ids } : {}
    );
    return { updated: response.updated ?? 0 };
  },

  /** SUPER_ADMIN only — pending (not yet exported) DR calculator request row count for sidebar badge */
  async getDrCalculatorPendingRequestsCount(): Promise<number> {
    const response = await apiService.get<{ success: boolean; count: number }>(
      '/competitor-backlinks/dr-calculator-requests/pending-count'
    );
    return typeof response.count === 'number' ? response.count : 0;
  },

  /** SUPER_ADMIN only — all DR calculator site requests (requestStatus=2), with client, project, assignedAdmins */
  async getDrCalculatorSiteRequests(): Promise<CompetitorBacklink[]> {
    const response = await apiService.get<{ success: boolean; data: CompetitorBacklink[] }>(
      '/competitor-backlinks/dr-calculator-requests'
    );
    return response.data;
  },

  /** Mark rows as exported (Exported tab). SUPER_ADMIN only (same as dr-calculator-requests). */
  async markDrCalculatorRequestsExported(ids: string[]): Promise<{ updated: number }> {
    const response = await apiService.post<{ success: boolean; updated: number }>(
      '/competitor-backlinks/dr-calculator-requests/mark-exported',
      { ids }
    );
    return { updated: response.updated ?? 0 };
  },

  /** Permanently delete DR calculator request rows (requestStatus=2). SUPER_ADMIN only. */
  async deleteDrCalculatorSiteRequests(ids: string[]): Promise<{ deleted: number }> {
    const response = await apiService.post<{ success: boolean; deleted: number }>(
      '/competitor-backlinks/dr-calculator-requests/delete',
      { ids }
    );
    return { deleted: response.deleted ?? 0 };
  },
};
